import { useQuery } from "@tanstack/react-query";
import { Scale } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { usePermission } from "@/hooks/usePermission";
import { weightSalesConfigAPI } from "@/lib/api";
import { BarcodeExample } from "@/features/settings/components/SettingsExamples";

export function ProductScalePanel({
	plu,
	onChange,
	readOnly,
	embedded = false,
}: {
	plu: string;
	onChange: (value: string) => void;
	readOnly: boolean;
	embedded?: boolean;
}) {
	const { t } = useTranslation();
	const canRead = usePermission("settings.read");
	const {
		data: config,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		queryKey: ["product-active-scale"],
		queryFn: weightSalesConfigAPI.getActive,
		enabled: canRead,
		retry: false,
	});
	return (
		<section
			className={`product-scale-panel${embedded ? " product-scale-inline" : " product-editor-surface"}`}
			data-testid="product-scale-panel"
		>
			<header className="flex items-start gap-3">
				<span className="scale-icon">
					<Scale aria-hidden className="h-5 w-5" />
				</span>
				<div>
					<h2 className="font-semibold">{t("productEditor.scaleTitle")}</h2>
				</div>
			</header>
			<div className="grid gap-4 sm:grid-cols-[minmax(0,220px)_1fr] items-end">
				<div className="space-y-2">
					<Label htmlFor="plu">{t("products.form.plu")}</Label>
					<Input
						id="plu"
						value={plu}
						onChange={e => onChange(e.target.value)}
						readOnly={readOnly}
						inputMode="numeric"
						dir="ltr"
						placeholder={t("products.form.pluPlaceholder")}
						className="text-center font-mono text-lg tracking-widest"
					/>
				</div>
				<p className="text-sm text-muted-foreground">
					{t("productEditor.scaleHint")}
					{canRead && (
						<Link
							className="mt-2 block text-primary underline underline-offset-4"
							to="/payments?type=scale"
						>
							{t("productEditor.scaleSettings")}
						</Link>
					)}
				</p>
			</div>
			{!canRead ? (
				<p className="text-sm text-muted-foreground">{t("productEditor.scaleRestricted")}</p>
			) : isLoading ? (
				<p role="status" className="text-sm text-muted-foreground">
					{t("common.loading")}
				</p>
			) : isError ? (
				<div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
					<p>{t("productEditor.scaleUnavailable")}</p>
					<Button variant="outline" onClick={() => refetch()}>
						{t("common.retry")}
					</Button>
				</div>
			) : config?.embeddedBarcodeEnabled ? (
				<BarcodeExample config={config} itemCode={plu} />
			) : (
				<p className="text-sm text-muted-foreground">{t("productEditor.scaleNotConfigured")}</p>
			)}
		</section>
	);
}
